export const healthcareSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 30198,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "Clinical Case: Ankle Injury Assessment",
					paragraphs: [
						{
							text: "A clinical scenario is presented. You see a description of a 45-year-old patient calling into a telehealth service with a complaint of ankle pain and swelling following a recent injury.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi74dkfUJXse7PNWJaohRIsq",
						url: "vi74dkfUJXse7PNWJaohRIsq",
						type: "video",
						transcript:
							"Hi, thanks for taking my call. I injured my right ankle a few days ago when I slipped while walking down the stairs. It's been swollen, and it hurts to walk on it. There's a lot of tenderness around the ankle, and I'm worried it might be something serious. I haven't had any fever or other issues, but it's not improving. Can you help me figure out what's wrong?",
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 30199,
				type: "MCSS",
				content: {
					parentId: 30198,
					parentType: "SET",
					stem: "Based on the patient's symptoms and history, what diagnostic test would you order to confirm the suspected underlying condition?",
					choices: [
						{
							id: "100f17b1-2f1e-4d07-af6b-f17de7857933",
							stem: "X-ray of the right ankle",
							text: "X-ray of the right ankle",
							isCorrect: true,
							explanation: `An **X-ray of the right ankle** is the most appropriate diagnostic test for this patient. **X-rays are the standard first-line imaging tool** for evaluating bone injuries, particularly **fractures and dislocations**. Given the patient's symptoms—**pain, swelling, and difficulty walking after a fall**—an X-ray is the best choice to quickly confirm or rule out a fracture. It is widely available, cost-effective, and provides **a clear view of bone structures** with minimal delay.

While other imaging options exist, they are **not the preferred initial choice** in this scenario. **An MRI of the right ankle** is useful for detecting **soft tissue injuries**, such as ligament or tendon damage, but it is **not necessary as a first step** when a fracture is the primary concern. **Ultrasound imaging** can help assess **soft tissue swelling or fluid buildup**, but it does **not provide a clear view of bones**, making it insufficient for detecting fractures. **A CT scan**, while offering more detailed imaging, is **typically reserved for complex cases** where a fracture is not clearly visible on an X-ray or when further anatomical detail is required.

For **an initial assessment of bone integrity**, an **X-ray remains the most effective and efficient choice.**

**Key Takeaways:**
- **X-rays are the first-line imaging test for suspected fractures** because they are quick, reliable, and provide clear bone visualization.
- **Other imaging methods, like MRI or CT, are useful in specific cases** but are generally not needed as the first step when evaluating a possible ankle fracture.

**References:**
- American College of Radiology (ACR). (2023). *ACR Appropriateness Criteria for Acute Ankle Pain*. Retrieved from [https://www.acr.org/Clinical-Resources/ACR-Appropriateness-Criteria/Acute-Ankle-Pain](https://www.acr.org/Clinical-Resources/ACR-Appropriateness-Criteria/Acute-Ankle-Pain)
- Smithuis, R. (2020). *Ankle trauma and fractures on radiographs*. Radiology Assistant. Retrieved from [https://radiologyassistant.nl/musculoskeletal/ankle/ankle-trauma](https://radiologyassistant.nl/musculoskeletal/ankle/ankle-trauma)
- Gunn, M. L., & O'Connor, A. R. (2017). *Diagnostic radiology: A survival guide* (3rd ed.). Elsevier. Retrieved from [https://www.elsevier.com/books/diagnostic-radiology-a-survival-guide/gunn/9780702067002](https://www.elsevier.com/books/diagnostic-radiology-a-survival-guide/gunn/9780702067002)`,
							numFirstSelections: 146,
						},
						{
							id: "ea2fb96d-a000-435d-b5e1-5617853f0c54",
							stem: "MRI of the right ankle",
							text: "MRI of the right ankle",
							isCorrect: false,
							numFirstSelections: 42,
						},
						{
							id: "ea2fb96d-a000-435d-b5e1-5617853f0c54-ged",
							stem: "Ultrasound of the right ankle",
							text: "Ultrasound of the right ankle",
							isCorrect: false,
							numFirstSelections: 42,
						},
						{
							id: "9bd9f1ff-0001-4704-8fab-b98742b29e05",
							stem: "CT scan of the right ankle",
							text: "CT scan of the right ankle",
							isCorrect: false,
							numFirstSelections: 34,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Vocabulary"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "100f17b1-2f1e-4d07-af6b-f17de7857933",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 30199,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.", // Using original instruction
				passage: {
					title: "Clinical Case: Ankle Injury Assessment",
					paragraphs: [
						{
							text: "A clinical scenario is presented. You see a description of a 45-year-old patient calling into a telehealth service with a complaint of ankle pain and swelling following a recent injury.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi74dkfUJXse7PNWJaohRIsq",
						url: "vi74dkfUJXse7PNWJaohRIsq",
						type: "video",
						transcript:
							"Hi, thanks for taking my call. I injured my right ankle a few days ago when I slipped while walking down the stairs. It's been swollen, and it hurts to walk on it. There's a lot of tenderness around the ankle, and I'm worried it might be something serious. I haven't had any fever or other issues, but it's not improving. Can you help me figure out what's wrong?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Integrated Writing Stimulus",
				topics: [],
			},
		},
		items: [
			{
				id: 306574,
				versionId: 1,
				content: {
					parentId: 30199,
					parentType: "SET",
					stem: "Based on the patient's description of his symptoms (pain, swelling, and difficulty walking after a fall), what is your preliminary diagnosis? Provide a detailed explanation supporting your conclusion, including the mechanism of injury, clinical findings, and any potential differential diagnoses.",
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Justification",
							icon: "file-document-outline",
							description:
								"Strengthen your diagnostic reasoning by developing clear, evidence-based arguments that link clinical findings to your assessment. Practice articulating the significance of key symptoms and their relationship to potential diagnoses.",
							maxScore: 5,
						},
						{
							name: "Differential Diagnosis",
							icon: "format-list-bulleted",
							description:
								"Develop a more comprehensive approach to differential diagnosis by methodically evaluating and excluding conditions based on presenting symptoms. Focus on building a systematic process for considering alternative diagnoses.",
							maxScore: 5,
						},
						{
							name: "Correct Diagnosis",
							icon: "check-circle-outline",
							description:
								"Enhance your diagnostic precision by improving how you integrate and analyze clinical information. Build stronger pattern recognition skills and clinical reasoning abilities to reach accurate conclusions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 30200,
			content: {
				instruction:
					"Watch the video and circle the abnormality visible in the image.",
				passage: {
					title: "Clinical Case: Ankle Injury Assessment",
					paragraphs: [
						{
							text: "A clinical scenario is presented. You see a description of a 45-year-old patient calling into a telehealth service with a complaint of ankle pain and swelling following a recent injury.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi74dkfUJXse7PNWJaohRIsq",
						url: "vi74dkfUJXse7PNWJaohRIsq",
						type: "video",
						transcript:
							"Hi, thanks for taking my call. I injured my right ankle a few days ago when I slipped while walking down the stairs. It's been swollen, and it hurts to walk on it. There's a lot of tenderness around the ankle, and I'm worried it might be something serious. I haven't had any fever or other issues, but it's not improving. Can you help me figure out what's wrong?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "READING",
				title: "Integrated Marking Answer Stimulus",
				topics: [],
			},
		},
		items: [
			{
				id: 306575,
				versionId: 1,
				content: {
					parentId: 30200,
					parentType: "SET",
					stem: "Using the X-ray provided, identify and circle the abnormality visible. Your task is to annotate the area that corresponds to the likely cause of the patient's symptoms.",
					choices: [],
					instruction:
						"Watch the video and circle the abnormality visible in the image.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/xray.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/xray.png",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description:
								"Improve your ability to precisely identify and mark abnormalities in medical imaging. Focus on developing keen attention to detail to ensure correct identification of pathological findings.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description:
								"Enhance your efficiency in analyzing and annotating medical images while maintaining diagnostic accuracy. Work on reducing annotation time without compromising the quality of your assessment.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 30201,
			content: {
				instruction: "Watch the video and record your answer.",
				passage: {
					title: "Clinical Case: Ankle Injury Assessment",
					paragraphs: [
						{
							text: "A clinical scenario is presented. You see a description of a 45-year-old patient calling into a telehealth service with a complaint of ankle pain and swelling following a recent injury.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi74dkfUJXse7PNWJaohRIsq",
						url: "vi74dkfUJXse7PNWJaohRIsq",
						type: "video",
						transcript:
							"Hi, thanks for taking my call. I injured my right ankle a few days ago when I slipped while walking down the stairs. It's been swollen, and it hurts to walk on it. There's a lot of tenderness around the ankle, and I'm worried it might be something serious. I haven't had any fever or other issues, but it's not improving. Can you help me figure out what's wrong?",
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["LS Summary"],
			},
		},
		items: [
			{
				id: 305773434,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 30201,
					parentType: "SET",
					stem: `After reviewing the patient's symptoms and diagnostic findings, clearly explain their diagnosis and recommended treatment plan. Ensure your explanation is clinically accurate, appropriate for their condition, and delivered in a professional, empathetic manner. Use clear, non-technical language to enhance patient understanding and comfort.`,
					choices: [],
					instruction: "Watch the video and record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description:
								"Demonstrate effective verbal and non-verbal communication skills while building rapport and maintaining professional boundaries with patients.",
						},
						{
							maxScore: 5,
							name: "Empathy & Patient-Centered Care",
							icon: "heart-outline",
							description:
								"Show genuine understanding of patient concerns while providing individualized care that respects their values, preferences and needs.",
						},
						{
							maxScore: 5,
							name: "Counseling & Patient Education",
							icon: "human-male-board",
							description:
								"Effectively educate patients about their condition, treatment options, and self-care strategies using clear, accessible language.",
						},
						{
							maxScore: 5,
							name: "Professionalism & Ethical Conduct",
							icon: "briefcase-outline",
							description:
								"Maintain professional demeanor and ethical standards while demonstrating cultural competence and respect for patient autonomy.",
						},
						{
							maxScore: 5,
							name: "Organization & Logical Flow",
							icon: "file-tree-outline",
							description:
								"Present information in a clear, structured manner with smooth transitions between topics and appropriate pacing.",
						},
						{
							maxScore: 5,
							name: "Closure & Next Steps",
							icon: "arrow-right-circle-outline",
							description:
								"Effectively summarize key points, verify patient understanding, and clearly communicate follow-up plans and expectations.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["LS Summary"],
				},
				score: 0,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 30202,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Clinical Case: Ankle Injury Assessment",
					paragraphs: [
						{
							text: "A clinical scenario is presented. You see a description of a 45-year-old patient calling into a telehealth service with a complaint of ankle pain and swelling following a recent injury.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi74dkfUJXse7PNWJaohRIsq",
						url: "vi74dkfUJXse7PNWJaohRIsq",
						type: "video",
						transcript:
							"Hi, thanks for taking my call. I injured my right ankle a few days ago when I slipped while walking down the stairs. It's been swollen, and it hurts to walk on it. There's a lot of tenderness around the ankle, and I'm worried it might be something serious. I haven't had any fever or other issues, but it's not improving. Can you help me figure out what's wrong?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Integrated Writing Stimulus",
				topics: [],
			},
		},
		items: [
			{
				id: 30657412,
				versionId: 1,
				content: {
					parentId: 30202,
					parentType: "SET",
					stem: `Document your findings and submit a detailed report for billing purposes. Your report should justify the tests performed and the treatment plan based on medical necessity.
        `,
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Comprehensiveness",
							icon: "file-document-outline",
							description:
								"Enhance your ability to create thorough documentation that captures all essential elements of patient encounters, ensuring no critical details are omitted from your written reports.",
						},
						{
							maxScore: 5,
							name: "Medical Necessity",
							icon: "clipboard-check-outline",
							description:
								"Strengthen your skills in providing clear and compelling justification for medical services, demonstrating how each intervention directly addresses patient needs and aligns with clinical standards.",
						},
						{
							maxScore: 5,
							name: "Medical Decision Making (MDM)",
							icon: "brain",
							description:
								"Develop stronger clinical reasoning abilities by improving how you document your diagnostic process, risk assessments, and treatment rationales in patient records.",
						},
						{
							maxScore: 5,
							name: "Code Alignment",
							icon: "barcode",
							description:
								"Improve your proficiency in selecting and applying appropriate billing codes that accurately capture the complexity and scope of medical services while ensuring compliance with coding guidelines.",
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: [],
					taskType: "default",
				},
				score: null,
			},
		],
	},
];
